import React, { useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, Typography } from "@mui/material";
import { AccessTimeOutlined } from "@mui/icons-material";
import { translate } from "../../../../translate/translate";
import {
  Analysis,
  AnalysisResultExtractType,
  ProjectedInventoryExtractData,
} from "../../../../models/general/analysis";
import { BaseService } from "../../../../services/base-service";
import { URLS } from "../../../../services/app-urls";
import { ErrorComponent } from "../../../../components/error/error.component";
import { DialogBar, Foot, T, formatValue } from "./extract-shared";

/** Operator that opens each row of the equation. */
type Operator = "+" | "-" | "=";

const DialogResultProjectedInventory = (props: any) => {
  const { open, onClose, title, analysisResultIds, onOpenExtract } = props;

  const service = new BaseService<Analysis>(URLS.ANALYSIS);
  const [data, setData] = React.useState<ProjectedInventoryExtractData>();
  const [isProcessingError, setIsProcessingError] = React.useState<boolean>(false);

  const handleCloseDialog = () => {
    onClose();
  };

  useEffect(() => {
    getProjectedInventoryInfo();
  }, [analysisResultIds]);

  const getProjectedInventoryInfo = () => {
    if (analysisResultIds.length === 0) return;
    setIsProcessingError(false);
    service.clearParameters();
    service.addParameter("analysis_result_ids", analysisResultIds);
    service.addParameter("extract_type", AnalysisResultExtractType.PROJECTED_INVENTORY);
    service
      .getAllFromListRoute("extract")
      .then((response: any) => {
        setData(response?.data);
      })
      .catch((error: any) => {
        if (error?.response?.status === 409 || error?.response?.status === 404) {
          setIsProcessingError(true);
        } else {
          ErrorComponent(error);
        }
      });
  };

  const stockTotal = data?.stock?.total || 0;
  const inTransitTotal = data?.in_transit?.total || 0;
  const backlog = data?.backlog || 0;
  const total = data?.total || 0;
  const supply = stockTotal + inTransitTotal;
  // Net demand is derived so the equation always resolves to `total`.
  const demand = supply - backlog - total;

  const openExtract = (extractType?: AnalysisResultExtractType) => {
    if (extractType && onOpenExtract) onOpenExtract(extractType);
  };

  const EqRow = (params: {
    op: Operator;
    name: string;
    hint?: string;
    value: number;
    tone?: "neg" | "faint" | "accent" | "ink";
    result?: boolean;
    extractType?: AnalysisResultExtractType;
  }) => {
    const { op, name, hint, value, tone = "ink", result, extractType } = params;
    const valueColor =
      tone === "neg" ? T.neg : tone === "faint" ? T.inkFaint : tone === "accent" ? T.accentInk : T.ink;
    const clickable = Boolean(extractType && onOpenExtract);

    return (
      <Box
        onClick={clickable ? () => openExtract(extractType) : undefined}
        sx={{
          background: result ? T.accentSoft : T.surface,
          display: "grid",
          gridTemplateColumns: "18px 1fr auto",
          alignItems: "center",
          gap: "10px",
          padding: "9px 14px",
          cursor: clickable ? "pointer" : "default",
        }}
      >
        <Typography
          sx={{ fontFamily: T.mono, fontSize: "14px", fontWeight: 700, color: T.inkFaint, textAlign: "center" }}
        >
          {op}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: T.inkSoft }}>
          <Box component="b" sx={{ color: result ? T.accentInk : T.ink, fontWeight: 600 }}>
            {name}
          </Box>
          {hint ? ` · ${hint}` : ""}
        </Typography>
        <Typography
          sx={{
            fontFamily: T.mono,
            fontSize: "13px",
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            color: valueColor,
          }}
        >
          {formatValue(value)}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ "& .MuiDialog-paper": { width: "unset" } }}>
      <DialogBar label={translate.t("projected_inventory")} pn={title} onClose={handleCloseDialog} />

      <DialogContent sx={{ maxHeight: "450px", overflowY: "auto", padding: 0, background: T.surface }}>
        {isProcessingError ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 2, minHeight: "300px", width: 420 }}
          >
            <AccessTimeOutlined sx={{ fontSize: 64, color: "#CFD8DC" }} />
            <Typography sx={{ color: T.inkSoft, fontWeight: 600, textAlign: "center", fontSize: "16px", maxWidth: "360px" }}>
              {translate.t("cell_data_processing")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: 420 }}>
            {/* Each part reopens its source extract. */}
            <Box sx={{ padding: "16px 18px" }}>
              <Box
                sx={{
                  border: `1px solid ${T.line}`,
                  borderRadius: "6px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1px",
                  background: T.line,
                }}
              >
                <EqRow op="+" name={translate.t("stock")} value={stockTotal} extractType={AnalysisResultExtractType.STOCK} />
                <EqRow op="+" name={translate.t("in_transit")} value={inTransitTotal} extractType={AnalysisResultExtractType.IN_TRANSIT} />
                <EqRow op="-" name={translate.t("net_demand")} value={demand} tone="neg" extractType={AnalysisResultExtractType.FORECAST} />
                <EqRow op="-" name={translate.t("backlog_previous_week")} hint={translate.t("backlog_previous_week_hint")} value={backlog} tone="faint" />
                <EqRow op="=" name={translate.t("projected_inventory")} value={total} tone="accent" result />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      {!isProcessingError && (
        <Foot label={translate.t("projected_inventory")} value={`${formatValue(total)} ${translate.t("unit")}`} valueColor={T.accentInk} />
      )}
    </Dialog>
  );
};

export default DialogResultProjectedInventory;
